import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard,
  Briefcase,
  User,
  History,
  Settings as SettingsIcon,
  LogOut,
  GraduationCap,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import NotificationBell from "@/components/NotificationBell";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/oferty", label: "Wszystkie oferty", icon: Briefcase, testid: "nav-offers" },
  { to: "/profil", label: "Profil", icon: User, testid: "nav-profile" },
  { to: "/historia", label: "Historia wysyłek", icon: History, testid: "nav-history" },
  { to: "/ustawienia", label: "Ustawienia", icon: SettingsIcon, testid: "nav-settings" },
];

export default function Layout({ children }) {
  const loc = useLocation();
  const nav = useNavigate();
  const { logout, user } = useAuth();

  const doLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen bg-[#0B1120] text-slate-100 flex">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-slate-800 bg-slate-950/60 backdrop-blur-xl flex flex-col">
        <div className="px-6 py-6 border-b border-slate-800">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
              <div className="w-10 h-10 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <div className="font-display text-sm font-semibold tracking-tight">Kuratorium</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Jobs · MVP</div>
              </div>
            </Link>
            <NotificationBell />
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((n) => {
            const active = loc.pathname === n.to;
            const Icon = n.icon;
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  active
                    ? "bg-cyan-400/10 text-cyan-300 border border-cyan-400/20"
                    : "text-slate-400 hover:text-slate-100 hover:bg-slate-900"
                }`}
              >
                <Icon className="w-4 h-4" />
                {n.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <div className="px-3 py-2 text-xs text-slate-500">Zalogowano jako</div>
          <div className="px-3 pb-2 text-sm text-slate-200 truncate" data-testid="current-user-email">
            {user?.email}
          </div>
          <button
            onClick={doLogout}
            data-testid="logout-button"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Wyloguj
          </button>
        </div>
      </aside>

      <motion.main
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex-1 min-w-0 overflow-x-hidden"
        key={loc.pathname}
      >
        {children}
      </motion.main>
    </div>
  );
}
